import { useQuery } from '@apollo/client';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the grid
import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { ActionButton } from 'elements/eventView/atomic/ActionButton';
import {
    FormSubmissions,
    FormSubmissionsVariables
} from 'graphql/query/__generated__/FormSubmissions';
import { FORM_SUBMISSIONS_QUERY } from 'graphql/query/guideQuery';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useMemo } from 'react';
import { useCSVDownloader } from 'react-papaparse';
import { useParams } from 'react-router-dom';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const FormSubmissionsPage = (props: Props): JSX.Element => {
    const { eventFormId } = useParams() as { eventFormId: string };
    const { CSVDownloader } = useCSVDownloader();

    const sumbmissionsQuery = useQuery<FormSubmissions, FormSubmissionsVariables>(
        FORM_SUBMISSIONS_QUERY,
        { variables: { eventFormId } }
    );

    const colWidth = 1260 / (sumbmissionsQuery?.data?.formSubmissions.headers?.length || 1) - 1;

    const columns = sumbmissionsQuery?.data?.formSubmissions.headers.map((e, i) => ({
        colId: i.toString(),
        field: e,
        width: colWidth,
        filter: 'agTextCellFilter',
        cellStyle: { 'white-space': 'pre', 'line-height': '22px' },
        autoHeight: true
    }));

    const duplicatedKeys = columns
        ?.map((e) => (columns.filter((f) => f.field === e.field).length > 1 ? e.field : null))
        .filter((e) => e !== null);

    const data = useMemo(() => {
        const resp = sumbmissionsQuery?.data?.formSubmissions.rows?.map((row) => {
            return Object.fromEntries(
                row.map((cell, i) => {
                    const key = duplicatedKeys.includes(columns[i].field)
                        ? columns[i].field + '_' + i
                        : columns[i].field;
                    return [key, cell ?? ''];
                })
            );
        });
        return resp;
    }, [sumbmissionsQuery.data]);

    // Create an instance ref that will hold the reference to React Table instance.
    if (sumbmissionsQuery.loading || !sumbmissionsQuery.data) {
        return null;
    }

    return (
        <S_FormSubmissionsPage className={props.className}>
            <S_Content className="ag-theme-quartz">
                <S_Downloader>
                    <CSVDownloader
                        // type={Type.}
                        filename={
                            (sumbmissionsQuery.data?.formSubmissions?.eventForm?.title?.replaceAll(
                                ' ',
                                '_'
                            ) || 'form') + `${new Date().toISOString()}`
                        }
                        bom={true}
                        config={{
                            delimiter: ','
                        }}
                        data={data}
                    >
                        <S_ActionButton text="Download CSV" />
                    </CSVDownloader>
                </S_Downloader>
                <AgGridReact
                    pagination={false}
                    gridOptions={{ suppressFieldDotNotation: true }}
                    rowData={data}
                    columnDefs={columns}
                />
            </S_Content>
        </S_FormSubmissionsPage>
    );
};

export default FormSubmissionsPage;

const S_FormSubmissionsPage = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
    padding: 30px;
    background: #f6f9ff;
    min-width: 1260px;
    width: 100%;
`;

const S_Content = styled.div`
    margin: 20px auto 20px auto;
    width: 1260px;
    height: 800px;
`;

const S_Downloader = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
`;

const S_ActionButton = styled(ActionButton)``;
